import { Button } from '@heroui/react';
import { Trash2 } from 'lucide-react';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useHeroToast, useWarmup } from 'src/hooks';
import { useCreateLanguage, useDeleteLanguages, useGetLanguagesFromFirestore } from 'src/queries';
import { LanguageDataDocumentRequest, LanguageDataResponse } from 'src/types';

import { DataTable, ModalConfirmation, ModalForm, PageTitle, PageWrapper } from '../../../components';
import { CreateLanguageForm } from '../components/CreateLanguageForm';
import { getLanguagesColumns } from './columns';

const PAGE_SIZE = 20;

export const Languages: FC = () => {
  const { showSuccess, showError } = useHeroToast();
  const [showNewLangModal, setShowNewLangModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Cold start funkcji v3 potrafi trwać kilka sekund — budzimy je przy wejściu na widok.
  useWarmup(['v3-createLanguage', 'v3-updateLanguage', 'v3-deleteLanguages']);

  const {
    data: languages,
    isLoading,
    isError,
    refetch,
  } = useGetLanguagesFromFirestore(currentPage, PAGE_SIZE);

  const rows = useMemo(() => languages?.data ?? [], [languages]);

  const { mutate: create, isPending } = useCreateLanguage();
  const { mutate: deleteLanguages, isPending: isDeleting } = useDeleteLanguages();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Języki wskazane do usunięcia: single = 1 element, bulk = zaznaczone.
  const [languagesToDelete, setLanguagesToDelete] = useState<LanguageDataResponse[]>([]);

  // Zaznaczenie dotyczy widocznej strony — po jej zmianie czyścimy je, żeby bulk delete
  // nie skasował języków, których użytkownik już nie widzi.
  useEffect(() => {
    setSelectedIds([]);
  }, [currentPage]);

  const selectedLanguages = useMemo(
    () => rows.filter((language) => selectedIds.includes(language.id)),
    [rows, selectedIds],
  );

  const areAllSelected = rows.length > 0 && selectedIds.length === rows.length;
  const isSomeSelected = selectedIds.length > 0;

  const handleToggleRow = (id: string): void => {
    setSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  const handleToggleAll = (): void => {
    setSelectedIds((prev) => (
      prev.length === rows.length ? [] : rows.map((language) => language.id)
    ));
  };

  const handleSubmit = async (data: LanguageDataDocumentRequest): Promise<void> => {
    create(
      { data },
      {
        onSuccess: () => {
          showSuccess('Language created successfully');
          setShowNewLangModal(false);
        },
        onError: (err) => {
          const { message } = (err.response?.data ?? {}) as { message?: string };
          showError(message || 'Language creation failed');
        },
      },
    );
  };

  // Single i bulk lecą tym samym endpointem — single to tablica jednoelementowa.
  const handleDelete = (): void => {
    const ids = languagesToDelete.map((language) => language.id);
    setLanguagesToDelete([]);

    deleteLanguages(
      { ids },
      {
        onSuccess: (result) => {
          setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));

          if (result.notFound.length > 0) {
            showError(`Nie znaleziono ${result.notFound.length} z ${ids.length} języków`);
            return;
          }

          showSuccess(
            result.deletedCount === 1
              ? 'Language deleted successfully'
              : `Usunięto ${result.deletedCount} języków`,
          );
        },
        onError: (err) => {
          const { message } = (err.response?.data ?? {}) as { message?: string };
          showError(message || 'Language deletion failed');
        },
      },
    );
  };

  const columns = useMemo(
    () => getLanguagesColumns({
      selectedIds,
      onToggleRow: handleToggleRow,
      onToggleAll: handleToggleAll,
      areAllSelected,
      isSomeSelected,
      onDelete: (language) => setLanguagesToDelete([language]),
      isDeleting,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedIds, rows, areAllSelected, isSomeSelected, isDeleting],
  );

  const isBulk = languagesToDelete.length > 1;

  return (
    <>
      <PageWrapper>
        <PageTitle
          title="Languages"
          buttonText="Add new"
          buttonOnClick={() => setShowNewLangModal(true)}
          actions={(
            <Button
              size="sm"
              color="danger"
              variant="flat"
              radius="sm"
              startContent={<Trash2 size={14} />}
              isDisabled={selectedLanguages.length === 0 || isDeleting}
              isLoading={isDeleting}
              onPress={() => setLanguagesToDelete(selectedLanguages)}
            >
              {`Usuń zaznaczone (${selectedLanguages.length})`}
            </Button>
          )}
        />

        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading || languages?.isLoading}
          isError={isError || !!languages?.error}
          onRefresh={refetch}
          pageSize={PAGE_SIZE}
          totalPages={languages?.totalPages ?? 1}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          searchPlaceholder="Search by language name or code..."
        />
      </PageWrapper>

      <ModalForm
        isLoading={isPending}
        title="Add new language"
        isOpen={showNewLangModal}
        form="create-language-form"
        onClose={() => setShowNewLangModal(false)}
      >
        <CreateLanguageForm
          formId="create-language-form"
          onSubmit={(e) => handleSubmit(e)}
        />
      </ModalForm>

      {/* Single: zostaje potwierdzenie wpisaniem kodu (jak dotąd). Bulk: samo
          potwierdzenie z liczbą — kodów jest wiele, nie ma czego wpisać. */}
      <ModalConfirmation
        needConfirm={!isBulk}
        confirmValue={languagesToDelete[0]?.code ?? ''}
        isOpen={languagesToDelete.length > 0}
        onClose={() => setLanguagesToDelete([])}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title={isBulk ? `Delete ${languagesToDelete.length} languages` : 'Delete language'}
        description={isBulk
          ? `Czy na pewno chcesz usunąć ${languagesToDelete.length} zaznaczonych języków? Operacja jest nieodwracalna.`
          : 'Are you sure you want to delete this language? This action will permanently remove the language and cannot be undone.'}
      />
    </>
  );
};
